/**
 * CUA (computer-use agent) test harness hooks, consumed by Playwright-driven
 * test clients:
 *
 * - `window.render_game_to_text()` → concise JSON of the current app state.
 * - `window.advanceTime(ms)` → deterministic frame stepping (runs the same
 *   per-frame hooks as the render loop at a fixed 60 Hz dt).
 * - Dev key bindings for history navigation, because test clients drive real
 *   keyboard input but can't call the history API: `b` → back, `a` → forward.
 */
import type { SceneCtx } from "./scene.ts";

export type CuaState = Record<string, unknown>;

declare global {
  interface Window {
    render_game_to_text?: () => string;
    advanceTime?: (ms: number) => void;
  }
}

export function installCua(ctx: SceneCtx, getState: () => CuaState): void {
  // Debug handle for harness-side scene interrogation (dev tooling only).
  (globalThis as unknown as Record<string, unknown>).__ctx = ctx;
  (globalThis as unknown as Window).render_game_to_text = () =>
    JSON.stringify({
      coords: "y up; x/z horizontal grid, unit 1.0; rotations in quarter turns",
      hash: location.hash,
      paused: !!ctx.paused,
      ...getState(),
    });

  (globalThis as unknown as Window).advanceTime = (ms: number) => {
    const steps = Math.max(1, Math.round(ms / (1000 / 60)));
    for (let i = 0; i < steps; i++) {
      for (const fn of ctx.onFrame) {
        fn(ctx.paused ? 0 : 1 / 60, performance.now() / 1000);
      }
    }
    ctx.renderer.render(ctx.scene, ctx.camera);
  };

  addEventListener("keydown", (e) => {
    const target = e.target as HTMLElement | null;
    if (
      target?.isContentEditable ||
      target?.closest("input, textarea, select, button, summary")
    ) return;
    if (e.code === "KeyB") history.back();
    else if (e.code === "KeyA") history.forward();
  });
}
